import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/store/redux";
import { selectUser } from "@/store/redux/slices/authSlice";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  ShieldOutlined as MfaIcon,
  LockOutlined as LockoutIcon,
  PasswordOutlined as PasswordIcon,
  EmailOutlined as EmailIcon,
  VpnKeyOutlined as ChangePasswordIcon,
  CloudOutlined as SsoIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import PageLayout from "@/components/PageLayout";
import { ROUTES } from "@/routes/config";
import SecurityForm from "./SecurityForm";
import ChangePasswordForm from "./ChangePasswordForm";
import SsoSettingsForm from "./SsoSettingsForm";
import { useSystemSettings, useUpdateSystemSettings } from "./useSystemSettings";

interface NavItem {
  label: string;
  description: string;
  icon: React.ReactElement;
  tabIndex: number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

function buildNavGroups(hasPassword: boolean): NavGroup[] {
  const groups: NavGroup[] = [
    {
      title: "SECURITY",
      items: [
        {
          label: "MFA",
          description: "Two-factor authentication",
          icon: <MfaIcon fontSize="small" />,
          tabIndex: 0,
        },
        {
          label: "Account Lockout",
          description: "Brute force protection",
          icon: <LockoutIcon fontSize="small" />,
          tabIndex: 1,
        },
        {
          label: "Passwords",
          description: "Policy, history & reset",
          icon: <PasswordIcon fontSize="small" />,
          tabIndex: 2,
        },
        {
          label: "Email & SMTP",
          description: "Outbound email config",
          icon: <EmailIcon fontSize="small" />,
          tabIndex: 3,
        },
        {
          label: "SSO",
          description: "Microsoft Entra ID",
          icon: <SsoIcon fontSize="small" />,
          tabIndex: 5,
        },
      ],
    },
  ];

  if (hasPassword) {
    groups.push({
      title: "ACCOUNT",
      items: [
        {
          label: "Change Password",
          description: "Update your password",
          icon: <ChangePasswordIcon fontSize="small" />,
          tabIndex: 4,
        },
      ],
    });
  }

  return groups;
}

const SystemSettings: React.FC = () => {
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const hasPassword = user?.hasPassword !== false;
  const navGroups = useMemo(() => buildNavGroups(hasPassword), [hasPassword]);
  const initialTab = useMemo(() => {
    const t = (location.state as { tab?: number } | null)?.tab;
    return typeof t === "number" ? t : 0;
  }, [location.state]);
  const [tab, setTab] = useState(initialTab);
  const { data: settings, isLoading, error } = useSystemSettings();
  const updateMutation = useUpdateSystemSettings();

  return (
    <PageLayout title="System Settings" backTo={ROUTES.DASHBOARD_HOME}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <SideNav activeTab={tab} onSelect={setTab} groups={navGroups} />

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            minWidth: 0,
          }}
        >
          <Box sx={{ p: 3 }}>
            {tab <= 3 && isLoading && (
              <Stack direction="row" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            )}

            {tab <= 3 && error && (
              <Alert severity="error">
                Failed to load system settings.
              </Alert>
            )}

            {tab <= 3 && settings && (
              <SecurityForm
                settings={settings}
                onSave={(data) => updateMutation.mutate(data)}
                isUpdating={updateMutation.isPending}
                activeTab={tab}
              />
            )}

            {tab === 4 && <ChangePasswordForm />}

            {tab === 5 && <SsoSettingsForm />}
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
};

interface SideNavProps {
  activeTab: number;
  onSelect: (index: number) => void;
  groups: NavGroup[];
}

const SideNav: React.FC<SideNavProps> = ({ activeTab, onSelect, groups }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        "& .MuiListItemButton-root": { my: 0, mx: 0.5 },
      }}
    >
      {groups.map((group) => {
        const isOpen = !collapsed[group.title];
        const hasActive = group.items.some((i) => i.tabIndex === activeTab);
        return (
          <Box key={group.title} sx={{ mb: 0.25 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                pt: 0.75,
                pb: 0.25,
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() =>
                setCollapsed((prev) => ({
                  ...prev,
                  [group.title]: !prev[group.title],
                }))
              }
            >
              <Typography
                variant="overline"
                sx={{
                  color: hasActive ? "text.primary" : "text.secondary",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  lineHeight: 1,
                }}
              >
                {group.title}
              </Typography>
              <IconButton
                size="small"
                sx={{
                  p: 0.25,
                  transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.15s",
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.items.map((item) => {
                  const selected = activeTab === item.tabIndex;
                  return (
                    <ListItemButton
                      key={item.label}
                      selected={selected}
                      onClick={() => onSelect(item.tabIndex)}
                      sx={{ py: 0.5, alignItems: "flex-start" }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, mt: 0.25 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={item.description}
                        primaryTypographyProps={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          lineHeight: 1.15,
                        }}
                        secondaryTypographyProps={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          sx: { mt: 0, lineHeight: 1.2 },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};

export default SystemSettings;
